import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy, HostBinding } from '@angular/core';

/**
 * Tenant-brand-colored button. Uses plain Tailwind + the global --tenant-accent
 * CSS var instead of Angular Material's button theme, since Material's MDC
 * color tokens are fixed at build time and can't track a per-tenant runtime color.
 */
@Component({
  selector: 'app-brand-button',
  imports: [],
  templateUrl: './brand-button.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './brand-button.component.scss',
})
export class BrandButtonComponent {
  @Input()
  variant: 'filled' | 'outline' | 'icon' | 'clear' = 'filled';

  /**
   * Semantic color override. 'brand' (default) follows the tenant accent; 'danger' and
   * 'success' re-point the accent vars on this button only, so every variant recolors.
   */
  @Input()
  tone: 'brand' | 'danger' | 'success' = 'brand';

  @HostBinding('style.--tenant-accent') get toneAccent(): string | null {
    return this.tone === 'danger' ? '#dc2626' : this.tone === 'success' ? '#16a34a' : null;
  }

  @HostBinding('style.--tenant-accent-contrast') get toneContrast(): string | null {
    return this.tone === 'brand' ? null : '#ffffff';
  }

  /** 'sm' is a compact button (40px) for dense areas such as cards and toolbars. */
  @Input()
  size: 'md' | 'sm' = 'md';

  get padding(): string {
    return this.size === 'sm' ? 'px-4 py-1.5 text-sm' : 'px-5 py-2.5';
  }

  @Input()
  label: string | null = null;

  @Input()
  icon: string | null = null;

  @Input()
  iconPos: 'left' | 'right' = 'right';

  /**
   * Icon hover animation:
   *  - 'slide' : nudge toward iconPos (good for "View all →" style buttons)
   *  - 'spin'  : rotate 180° (good for a refresh icon)
   *  - 'lift'  : small scale-up
   *  - 'none'  : no icon motion (default for icon-only buttons)
   */
  @Input()
  iconHover: 'slide' | 'spin' | 'lift' | 'none' = 'slide';

  get iconHoverClass(): string {
    // Icon-only buttons should not slide by default — the nudge looks broken on
    // a lone icon. Honor an explicit non-slide choice (e.g. 'spin') though.
    const effective =
      (this.variant === 'icon' || this.variant === 'clear') && this.iconHover === 'slide'
        ? 'none'
        : this.iconHover;

    switch (effective) {
      case 'spin':
        return 'transition-transform duration-300 group-enabled:group-hover:rotate-180';
      case 'lift':
        return 'transition-transform duration-200 group-enabled:group-hover:scale-110';
      case 'none':
        return '';
      case 'slide':
      default:
        return this.iconPos === 'left'
          ? 'transition-transform duration-200 group-enabled:group-hover:-translate-x-1'
          : 'transition-transform duration-200 group-enabled:group-hover:translate-x-1';
    }
  }

  @Input()
  rounded = true;

  @Input()
  fullWidth = false;

  @HostBinding('class.block') get hostBlock() { return this.fullWidth; }
  @HostBinding('class.w-full') get hostFull() { return this.fullWidth; }

  @Input()
  disabled = false;

  @Input()
  ariaLabel: string | null = null;

  @Input()
  type: 'button' | 'submit' = 'button';

  @Output()
  btnClick: EventEmitter<MouseEvent> = new EventEmitter();

  onClick(event: MouseEvent) {
    if (this.disabled) {
      return;
    }

    this.btnClick.emit(event);
  }
}
